import s from './Gift.module.sass';

import { FC } from 'react';

import { useSelector } from 'react-redux';
import { animated } from '@react-spring/web';

import { useCurrentUserInfo, useDesktop, useTranslator, useUserInfo } from '$hooks';
import { LottieIcon } from '$uikit';
import { CurrencyEnum, GiftType, MessageModel } from '$store/models';
import { imAsPeerIdSelector } from '$store/im';
import { IcGift28, IcShare24 } from '$assets';
import { classNames, formatCurrency } from '$utils';
import { showModal } from '$navigation/helper';
import { REPLY_SWIPE_THRESHOLD, useReplySwipe } from '$core/Chat/useReplySwipe';

export const Gift: FC<{
  message: MessageModel;
  peerId: number;
  isHighlighted?: boolean;
}> = (props) => {
  const { message, peerId, isHighlighted } = props;
  const peer = useUserInfo(peerId);
  const curUser = useCurrentUserInfo();
  const asPeerId = useSelector(imAsPeerIdSelector);
  const isDesktop = useDesktop();
  const isInbox = asPeerId ? message.fromId !== asPeerId : message.fromId !== curUser?.id;

  const gift = message.attachments![0].gift!;

  const t = useTranslator();

  const canReply = message.id > 0 && !message.isSending && !message.isFailed;
  const { bindSwipe, x, didSwipe } = useReplySwipe(
    peerId,
    message,
    !isDesktop && canReply,
  );

  const handleClick = () => {
    if (didSwipe.current) {
      return;
    }

    if (peer?.verified === 2) {
      showModal(`gift${peerId}`);
    }
  };

  function renderGift() {
    if (gift.type === GiftType.Static) {
      return (
        <div
          className={s.gift}
          style={{
            backgroundImage: `url(${gift.url})`,
          }}
        />
      );
    } else if (gift.type === GiftType.Animated) {
      return (
        <div
          className={s.gift}
          style={{
            backgroundImage: `url(${gift.url})`,
          }}
        >
          <LottieIcon name={gift.url} loop className={s.lottieGift} />
        </div>
      );
    }
  }

  return (
    <div
      className={classNames(s.wrap, {
        [s.inbox]: isInbox,
        [s.outbox]: !isInbox,
        [s.highlighted]: !!isHighlighted,
      })}
    >
      {!isDesktop && canReply && (
        <animated.div
          className={s.replySwipeIcon}
          style={{ opacity: x.to((v) => Math.min(1, -v / REPLY_SWIPE_THRESHOLD)) }}
        >
          <IcShare24 />
        </animated.div>
      )}
      <animated.div className={s.in} style={{ x }} {...bindSwipe()} onClick={handleClick}>
        {renderGift()}
        <div className={s.title}>
          <IcGift28 />
          <div className={s.titleLabel}>{t('dialog_attach_gift')}</div>
        </div>
        <div className={s.price}>{formatCurrency(message.price, CurrencyEnum.EUR)}</div>
      </animated.div>
    </div>
  );
};
